import { notFound } from 'next/navigation';

import { getAsset, getChannel, getDocument } from '@lib/server';
import type { Aggregator, Place, SocialWithContact } from '@types';

import { getOrganisation } from '../get-organisation';

export async function getBrandLayoutData(domain: string) {
  const channel = await getChannel(domain);
  const org = await getOrganisation();

  if (!channel?.channelId || !channel?.micrositeData?.[0]?.id) notFound();

  const { fields, id } = await getDocument<Aggregator>('aggregator', channel.micrositeData?.[0].id, {
    select: [
      'fields.id',
      'fields.brandLogo',
      'fields.backgroundImage',
      'fields.backgroundImageOpacity',
      'fields.bookingTypes',
      'fields.venuesPageSlug',
      'fields.name',
      'fields.facebook',
      'fields.instagram',
      'fields.twitter',
      'fields.emailAddress',
      'fields.phoneNumber',
      'fields.navigationActions',
      'fields.tikTok',
      'fields.youTube',
      'fields.googlePlace',
      'fields.tripadvisor',
      'fields.mainNavigation',
      'fields.footerNavigation',
      'fields.subFooterNavigation',
      'fields.seo',
    ],
    depth: 0,
    tags: [channel.channelId],
  });

  const announcements = await getDocument<Aggregator>('aggregator', channel.micrositeData?.[0]?.id, {
    depth: 1,
    select: ['fields.brandAnnouncements'],
    tags: [channel.channelId],
  }).then((res) => res?.fields?.brandAnnouncements);

  const favicon = await getAsset(channel?.favicon?.[0]?.id);
  const operatorLogo = (await getAsset(org?.logo?.[0]?.id)) || undefined;

  if (!fields || !id) notFound();

  let middleColumnContent;
  let address;
  let venueParam;
  let schema;
  if (channel.places?.length === 1) {
    const place = await getDocument<Place>('place', channel.places?.[0].id, {
      tags: [channel.channelId],
      depth: 1,
      status: 'published',
      select: ['fields.custom.venueFooter', 'fields.default', 'fields.custom.slug'],
    });

    venueParam = place.fields?.custom?.slug;
    middleColumnContent = place.fields?.custom?.venueFooter?.[0]?.middleColumn;
    address = [
      place?.fields?.default.address?.addressLine1,
      place?.fields?.default.address?.addressLine2,
      place?.fields?.default.address?.cityOrTown,
      place?.fields?.default.address?.country,
      place?.fields?.default.address?.county,
      place?.fields?.default.address?.postcode,
    ]
      .filter(Boolean)
      .join(', ');
    schema = place.fields?.default;
  }

  const footerLinks = [
    ...(fields.footerNavigation || [])?.map((link) => ({
      ...link,
    })),
  ].filter(Boolean);

  const navigationLinks = [
    ...(fields.mainNavigation || [])?.map((link) => ({
      ...link,
    })),
  ].filter(Boolean);

  const subFooterLinks = [
    ...(fields.subFooterNavigation || [])?.map((link) => ({
      ...link,
    })),
  ].filter(Boolean);

  const seo = {
    title: fields.seo?.title || fields.name,
    description: fields.seo?.description || fields.brandSummary,
    media: fields.seo?.media || fields.homepageHeroBanner?.slides?.[0]?.image?.desktop,
    hideEntry: fields.seo?.hideEntry,
  };

  return {
    logo: fields.brandLogo,
    channelId: channel.channelId,
    placeId: channel.places?.map((place) => place.id),
    seo,
    navigationActions: fields.navigationActions,
    siteVerification: channel.apps?.siteVerifcation?.active ? channel.apps?.siteVerifcation.id : undefined,
    siteName: fields.name,
    backgroundImage: fields.backgroundImage,
    backgroundImageOpacity: fields.backgroundImageOpacity,
    bookingTypes: fields.bookingTypes?.filter((type) => type.showAtBrandLevel),
    favicon,
    socials: {
      facebook: fields.facebook,
      instagram: fields.instagram,
      twitter: fields.twitter,
      emailAddress: fields.emailAddress,
      phoneNumber: fields.phoneNumber,
      tikTok: fields.tikTok,
      youTube: fields.youTube,
      googlePlace: fields.googlePlace,
      tripadvisor: fields.tripadvisor,
      designMyNight: fields.designMyNight,
      linkedIn: fields.linkedIn,
    } as SocialWithContact,
    theme: channel?.themeConfig,
    channelApps: channel?.apps,
    navigationLinks,
    transitionName: channel.themeConfig?.animations,
    announcements,
    address,
    venueParam,
    footer: {
      operatorLogo,
      operatorHref: org?.website
        ? org.website.startsWith('http')
          ? org.website
          : `https://${org.website}`
        : undefined,
      footerLinks,
      subFooterLinks,
      middleColumnContent,
    },
    '404Page': {
      pageContent: fields['404PageContent'],
      heroBanner: fields['404PageHeroBanner'],
    },
    schema,
  };
}
